import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  
  const paymentStatus = searchParams.get("paymentStatus") || searchParams.get("status");
  const orderId = searchParams.get("merchantOrderId") || searchParams.get("order_id") || "";
  const transactionId = searchParams.get("transactionId") || "";
  const amount = searchParams.get("amount") || "";
  const currency = searchParams.get("currency") || "EGP";
  const cardBrand = searchParams.get("cardBrand") || "";
  const maskedCard = searchParams.get("maskedCard") || "";

  useEffect(() => {
    // Verify payment with backend
    if (paymentStatus === "SUCCESS" || paymentStatus === "success") {
      setStatus("success");
      // Update order status in backend
      const params = new URLSearchParams(window.location.search);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment/verify?${params.toString()}`);
    } else if (paymentStatus === "FAILURE" || paymentStatus === "failed") {
      setStatus("failed");
    } else {
      setStatus("success");
    }
  }, [paymentStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950" dir="ltr">
      <Navbar />
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-2xl mx-auto">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-center">
                  {status === "loading" && (
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="h-16 w-16 text-yellow-400 animate-spin" />
                      <span className="text-2xl text-white">Processing payment...</span>
                    </div>
                  )}
                  {status === "success" && (
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-4 bg-green-500/20 rounded-full">
                        <CheckCircle className="h-16 w-16 text-green-400" />
                      </div>
                      <span className="text-3xl text-white">Payment Successful! 🎉</span>
                    </div>
                  )}
                  {status === "failed" && (
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-4 bg-red-500/20 rounded-full">
                        <XCircle className="h-16 w-16 text-red-400" />
                      </div>
                      <span className="text-3xl text-white">Payment Failed</span>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {status === "success" && (
                  <>
                    <div className="text-center space-y-4">
                      <p className="text-lg text-gray-300">
                        Thank you! Your order has been received and your service will be activated shortly.
                      </p>
                      
                      {/* Order Details */}
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10 space-y-3 text-left">
                        {orderId && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Order ID</span>
                            <span className="text-white font-mono text-sm">{orderId}</span>
                          </div>
                        )}
                        {transactionId && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Transaction ID</span>
                            <span className="text-white font-mono text-sm">{transactionId}</span>
                          </div>
                        )}
                        {amount && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Amount</span>
                            <span className="text-white font-semibold">{amount} {currency}</span>
                          </div>
                        )}
                        {cardBrand && maskedCard && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Card</span>
                            <span className="text-white">{cardBrand} {maskedCard}</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                        <p className="text-sm text-yellow-300">
                          📧 We will send you an email with your order details and login credentials shortly.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/" className="flex-1">
                        <Button className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold">
                          Back to Home
                        </Button>
                      </Link>
                      <Link to="/dashboard" className="flex-1">
                        <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                          Dashboard
                        </Button>
                      </Link>
                    </div>
                  </>
                )}
                {status === "failed" && (
                  <>
                    <div className="text-center space-y-4">
                      <p className="text-lg text-gray-300">
                        Sorry, payment was not completed. Please try again or contact support.
                      </p>
                      {transactionId && (
                        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Reference</span>
                            <span className="text-white font-mono text-sm">{transactionId}</span>
                          </div>
                        </div>
                      )}
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <p className="text-sm text-red-300">
                          If the amount was deducted from your account, please contact us immediately.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/plans" className="flex-1">
                        <Button className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold">
                          Try Again
                        </Button>
                      </Link>
                      <Link to="/contact" className="flex-1">
                        <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                          Contact Us
                        </Button>
                      </Link>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
